import { useEffect, useState } from 'react';

import { useNearbySend } from '@/hooks/useNearbySend';
import { tr, useT } from '@/i18n';

import { Button } from './Button';
import { CheckIcon } from './Icons';
import { EmojiReels } from './EmojiReels';
import { closeText, PeerAvatar, useNearby } from './Nearby';
import styles from './nearby.module.css';
import type { NearbySendProps } from './NearbySend.types';

/** After this long without anyone around, say what usually keeps a device out of sight. */
const HINT_AFTER_MS = 6000;

/**
 * Share → Nearby: the devices on this Wi-Fi with Bridge open, and sending the
 * bridge to one of them. The offer carries a commitment to this device's
 * public key; the other device answers with its own, this device then shows
 * its key, both screens show the same four emoji, and once it accepts, the
 * bridge key goes over wrapped for that device alone.
 */
export function NearbySend({ bridgeId, bridgeKey, ownerToken, viewsLeft, onSent, onCompare }: NearbySendProps): React.JSX.Element {
  const t = useT();
  const nearby = useNearby();
  const { step, send, confirm, cancel } = useNearbySend(nearby, bridgeId, bridgeKey, ownerToken, onSent);
  const [hint, setHint] = useState(false);

  // The footer button says the emoji match: tell it where this step stands, and withdraw it when the step goes away.
  const comparing = step.phase === 'offering' || step.phase === 'compare' || step.phase === 'sending';
  const status = !comparing ? null : step.phase === 'offering' || (step.phase === 'compare' && step.emoji === null) ? 'loading' : step.phase === 'compare' && !step.confirmed ? 'ready' : 'busy';
  const peerName = comparing ? step.peer.name : '';
  useEffect(() => {
    onCompare?.(status === null ? null : { status, peer: peerName, confirm });
  }, [onCompare, status, peerName, confirm]);
  useEffect(
    () => () => {
      onCompare?.(null);
    },
    [onCompare],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setHint(true);
    }, HINT_AFTER_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, []);

  if (nearby === null) return <p className={styles.listHint}>{t('nearbySend.unavailable')}</p>;
  if (nearby.full !== null) return <p className={styles.listHint}>{t('nearbySend.full', { message: nearby.full })}</p>;
  if (viewsLeft <= 0 && step.phase === 'list') {
    return (
      <p className={styles.listHint} role="alert">
        {t('share.noViewsLeft')}
      </p>
    );
  }

  let body: React.JSX.Element;
  if (step.phase === 'list') {
    body =
      nearby.peers.length === 0 ? (
        <div className={styles.radar} role="status" data-testid="nearby-looking">
          <span className={styles.radarRings} aria-hidden="true">
            <i />
            <i />
            <i />
            {nearby.me ? <PeerAvatar peer={nearby.me} size="large" /> : null}
          </span>
          <p className={styles.radarText}>{t('nearbySend.looking')}</p>
          {hint ? (
            <p className={styles.listHint}>
              {t('nearbySend.lookingHint')}
            </p>
          ) : null}
        </div>
      ) : (
        <div>
          <p className={styles.listLabel}>{t('nearbySend.onWifi')}</p>
          <ul className={styles.list}>
            {nearby.peers.map((peer) => (
              <li key={peer.id} className={styles.peer} data-testid="nearby-peer">
                <PeerAvatar peer={peer} />
                <span className={styles.peerText}>
                  <span className={styles.peerName}>{peer.name}</span>
                  <span className={styles.peerDevice}>{peer.device}</span>
                </span>
                <Button size="small" onClick={() => {
                  send(peer);
                }} aria-label={t('nearbySend.sendTo', { name: peer.name })}>
                  {t('common.send')}
                </Button>
              </li>
            ))}
          </ul>
          <p className={styles.listHint}>{t('nearbySend.listHint')}
            <br />
            {t('nearbySend.namesMadeUp')}</p>
        </div>
      );
  } else if (step.phase === 'offering' || step.phase === 'compare' || step.phase === 'sending') {
    const emoji = step.phase === 'offering' ? null : step.emoji;
    body = (
      <div className={styles.check} data-testid="nearby-compare">
        <p className={styles.checkTitle}>{t('nearbySend.compareTitle', { name: step.peer.name })}</p>
        <EmojiReels emoji={emoji} testId="nearby-emoji" />
        <p className={styles.checkText}>{t('nearbySend.compareText', { name: step.peer.name })}</p>
        {onCompare === undefined && step.phase === 'compare' && step.emoji !== null && !step.confirmed ? (
          <Button size="small" className={styles.matchButton} onClick={confirm} data-testid="nearby-match">
            {t('common.match')}
          </Button>
        ) : (
          <p className={styles.waiting} role="status">
            <i aria-hidden="true" />
            {step.phase === 'sending' ? t('share.sendingTo', { peer: step.peer.name }) : t('nearbySend.waitAccept', { name: step.peer.name })}
          </p>
        )}
        {step.phase === 'sending' ? null : (
          <button type="button" className={styles.textButton} onClick={cancel}>
            {t('common.cancel')}
          </button>
        )}
      </div>
    );
  } else if (step.phase === 'sent') {
    body = (
      <div className={styles.done} data-testid="nearby-sent">
        <span className={styles.doneMark} aria-hidden="true">
          <CheckIcon size={24} strokeWidth={2.4} />
        </span>
        <p className={styles.doneTitle}>{t('nearbySend.sentTo', { name: step.peer.name })}</p>
        <p className={styles.checkText}>{t('nearbySend.sentNote')}</p>
      </div>
    );
  } else {
    body = (
      <div className={styles.done} data-testid="nearby-closed">
        <p className={styles.doneTitle}>{t('nearbySend.notSent')}</p>
        <p className={styles.checkText} role="status">
          {step.reason === undefined && step.message !== undefined ? step.message : closeText(step.reason, step.peer.name)}
        </p>
        <Button
          variant="secondary"
          size="small"
          onClick={cancel}
        >
          {t('nearbySend.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.send} data-testid="nearby">
      {nearby.me ? (
        <p className={styles.you}>
          <PeerAvatar peer={nearby.me} />
          <span>
            {tr('nearbySend.thisDevice', { strong: (children) => <strong>{children}</strong> }, { name: nearby.me.name })}
            <span className={styles.youDevice}> · {nearby.me.device}</span>
          </span>
        </p>
      ) : null}
      <div className={styles.stage}>{body}</div>
    </div>
  );
}
